<script lang="ts">
  // The repository index (`/gh`): the repositories a person can review, in
  // the order state/repos.svelte.ts gives them, each linking to its
  // repository page. `?owner=` is one owner's view; `?view=branches` lists
  // the repositories with the viewer's branches without a pull request.
  import { onMount, untrack } from 'svelte';
  import LoadingGate from '../lib/loading/LoadingGate.svelte';
  import { oneStep } from '../lib/loading/steps';
  import { installLinkText, installUrl, readOnlyText } from '../lib/api/errors';
  import type { Failure } from '../lib/api/failure';
  import { relTime } from '../lib/logic/time';
  import { BRANCHES_READ, type ChecksDot, type RepoGroup, type RepoRow } from '../lib/logic/repos';
  import { GH, repoIndexPath, VIEW_PARAM } from '../lib/router';
  import { nav } from '../lib/nav.svelte';
  import { session } from '../lib/state/session.svelte';
  import { readOnlyOf, repoIndex, type CountsState } from '../lib/state/repos.svelte';
  import { MAX_REPO_PINS } from '../lib/jump/places.svelte';

  const index = repoIndex();
  onMount(() => void index.load());
  /** What a pin did, when it did not: past the cap of pinned repositories. */
  let pinSaid = $state('');
  function togglePin(r: RepoRow) {
    pinSaid = index.togglePin(r.key) ? '' : `${MAX_REPO_PINS} repositories pinned already: unpin one to pin ${r.owner}/${r.name}`;
  }

  const owner = $derived(nav.query.get('owner') || null);
  const branchesView = $derived(nav.query.get(VIEW_PARAM) === 'branches');
  // The window's title, as the location line names the page: its view, its owner, the index.
  const title = $derived([branchesView ? 'Your branches without a pull request' : null, owner, 'Repositories'].filter(Boolean).join(' · '));
  $effect(() => {
    document.title = title;
  });
  let text = $state('');

  const shown = $derived(index.filter({ text, owner }));
  /** In the Branches view: the repositories looked into, those the viewer pushed to recently or pinned (their branches are read at once). */
  const candidates = $derived(branchesView ? shown.filter((r) => r.viewerPushedAt || r.pinned) : []);
  /** Asked for once each time the repositories looked into change, not whenever a row's spelling or counts do: a failed read waits for Try again. */
  const candidateKeys = $derived(candidates.map((r) => r.key).join('\n'));
  $effect(() => {
    const keys = candidateKeys ? candidateKeys.split('\n') : [];
    if (keys.length) untrack(() => void index.see(keys));
  });
  const stateOf = (r: RepoRow) => index.countsOf(r.key);
  /** What a row says: while Try again reads it, what it said before, so it keeps its place (and its element) meanwhile. */
  const saidOf = (c: CountsState | undefined): CountsState | undefined => (c?.status === 'loading' && c.was ? c.was : c);
  /** Looked into, and it has branches of the viewer's without a pull request. */
  const found = $derived(candidates.filter((r) => { const c = saidOf(stateOf(r)); return c?.status === 'ready' && c.counts.myBranches.length > 0; }));
  /** Looked into, and GitHub did not say (it failed, refused, or does not show it): never read as "none". */
  const unknown = $derived(candidates.filter((r) => { const c = saidOf(stateOf(r)); return !!c && c.status !== 'ready' && c.status !== 'loading'; }));
  const checking = $derived(candidates.filter((r) => { const c = saidOf(stateOf(r)); return !c || c.status === 'loading'; }).length);

  const GROUPS: Record<RepoGroup, string> = { pinned: 'Pinned', waiting: 'Waiting on you', pushed: 'You pushed recently', rest: 'Everything else' };
  const grouped = (rows: RepoRow[]) => {
    const out: { id: string; title: string; rows: RepoRow[] }[] = [];
    for (const r of rows) {
      if (out.at(-1)?.id !== r.group) out.push({ id: r.group, title: GROUPS[r.group], rows: [] });
      out.at(-1)!.rows.push(r);
    }
    return out;
  };
  /** What the page lists, in sections: by group; best matches first while filtering; in the Branches view, what was found, then what could not be told. */
  const sections = $derived.by(() => {
    if (branchesView) {
      const out = text ? (found.length ? [{ id: 'matches', title: 'Matches', rows: found }] : []) : grouped(found);
      return unknown.length ? [...out, { id: 'unknown', title: "Couldn't look into", rows: unknown }] : out;
    }
    return text ? (shown.length ? [{ id: 'matches', title: 'Matches', rows: shown }] : []) : grouped(shown);
  });

  const installLink = $derived(installUrl(session.config?.app_slug, session.config?.github_web_url));
  const repoHref = (r: RepoRow) => `${GH}/${encodeURIComponent(r.owner)}/${encodeURIComponent(r.name)}`;
  const branchWord = (n: number, capped: boolean) => `${n}${capped ? '+' : ''} ${n === 1 && !capped ? 'branch' : 'branches'} without a pull request`;
  const CHECKS: Record<Exclude<ChecksDot, null>, string> = { good: 'checks passed', bad: 'checks failed', pending: 'checks running' };
  /** A failed read in plain words. */
  const failedWords = (f: Failure) =>
    f.cause === 'github_slow'
      ? 'GitHub took too long to count it'
      : f.cause === 'github_error'
        ? "GitHub couldn't count it"
        : f.cause === 'rate_limited'
          ? "GitHub's rate limit stopped the count"
          : f.cause === 'unreachable' || f.cause === 'offline'
            ? "Changeling couldn't be reached to count it"
            : "Couldn't count it";

  // Counts are read only for the rows on screen, a batch for those that come into view together.
  let due = new Set<string>();
  function flush() {
    const keys = [...due];
    due = new Set();
    if (keys.length) void index.see(keys);
  }
  function want(key: string) {
    if (!due.size) queueMicrotask(flush);
    due.add(key);
  }
  const io =
    typeof IntersectionObserver === 'undefined'
      ? null
      : new IntersectionObserver((entries) => {
          for (const e of entries) if (e.isIntersecting) want((e.target as HTMLElement).dataset.key!);
        }, { rootMargin: '200px 0px' });
  $effect(() => () => io?.disconnect());
  /** Reads a row's counts once it is on screen (at once, where nothing can tell). */
  function onscreen(node: HTMLElement) {
    if (!io) return void want(node.dataset.key!);
    io.observe(node);
    return { destroy: () => io.unobserve(node) };
  }

  // Back on the page (after installing the App, say): coverage is asked again, at most once a minute.
  function onVisible() {
    if (document.visibilityState === 'visible') void index.coverageSignal({ kind: 'focus' });
  }
</script>

<svelte:window
  onfocus={() => {
    void index.coverageSignal({ kind: 'focus' });
    void index.failuresSignal('focus');
  }}
  ononline={() => void index.failuresSignal('online')}
/>
<svelte:document onvisibilitychange={onVisible} />

{#snippet row(r: RepoRow, now: CountsState | undefined)}
  {@const c = saidOf(now)}
  {@const reading = now?.status === 'loading'}
  {@const ro = readOnlyOf(c)}
  {@const ready = c?.status === 'ready' ? c.counts : null}
  <!-- A pull request the dashboard last saw waiting on you may have closed since: never more of them than are open. -->
  {@const turn = ready ? Math.min(r.waiting, ready.openPulls) : r.waiting}
  <li class="pr-row repo-row" data-key={r.key} use:onscreen>
    <span class="dot {ready?.checks ?? ''}" aria-hidden="true"></span>
    <div class="pr-row-main">
      <a class="pr-row-title repo-name mono" href={repoHref(r)}>{r.owner}/{r.name}</a>
      <div class="pr-row-sub">
        {#if ready}
          <span>{ready.openPulls} open{#if turn}{' · '}<b class="turn">{turn} your turn</b>{/if}</span>
          {#if ready.myBranches.length}
            <a class="branches" href="{repoHref(r)}/branches" title={ready.branchesCapped ? `Among its ${BRANCHES_READ} most recently committed branches` : undefined}
              >{branchWord(ready.myBranches.length, ready.branchesCapped)}</a
            >
          {/if}
          {#if ready.checks}<span class="checks {ready.checks}">{CHECKS[ready.checks]} on {ready.defaultBranch ?? 'the default branch'}</span>{/if}
        {:else if turn}
          <span><b class="turn">{turn} your turn</b> on the dashboard</span>
        {/if}
        {#if reading}<span>counting…</span>{/if}
        {#if ready?.pushedAt ?? r.pushedAt}<span>pushed {relTime(ready?.pushedAt ?? r.pushedAt)}</span>{/if}
        {#if ro}<span class="pill neutral">read-only</span>{/if}
      </div>
      {#if c?.status === 'missing'}
        <p class="repo-why">GitHub doesn't show this repository to you: it may have been deleted, renamed, or made private.</p>
      {:else if c?.status === 'sso'}
        <p class="repo-why">Needs SSO authorization: authorize your SSO session for {r.owner} on GitHub, then <button class="micro" disabled={reading} onclick={() => void index.retry([r.key])}>Try again</button></p>
      {:else if c?.status === 'forbidden'}
        <p class="repo-why">GitHub refused to show it: {c.message} <button class="micro" disabled={reading} onclick={() => void index.retry([r.key])}>Try again</button></p>
      {:else if c?.status === 'failed'}
        <p class="repo-why">{failedWords(c.failure)}. <button class="micro" disabled={reading} onclick={() => void index.retry([r.key])}>Try again</button></p>
      {/if}
      {#if ro}
        <p class="repo-why">
          {readOnlyText(r.owner, r.name, ro)}
          {#if installLink && ro === 'not_installed'}<a class="install" href={installLink}>{installLinkText(r.owner)}</a>{/if}
        </p>
      {/if}
    </div>
    <button
      type="button"
      class="pin micro"
      aria-pressed={r.pinned}
      aria-label="Pin {r.owner}/{r.name}"
      title={r.pinned ? 'Unpin: list it in its usual place' : 'Pin: list it first, in this browser'}
      onclick={() => togglePin(r)}>{r.pinned ? '★' : '☆'}</button
    >
  </li>
{/snippet}

<header class="top">
  <!-- The page's heading, for its outline; to the eye, the top bar's location line names it. -->
  <h1 class="sr-only">{title}</h1>
  <div class="eyebrow">Repositories{index.source === 'viewer' ? ' · yours on GitHub' : ''}</div>
  <nav class="repo-views" aria-label="Repository views">
    <a class="micro" href={repoIndexPath({ owner })} aria-current={branchesView ? undefined : 'page'}>All repositories</a>
    <a class="micro" href={repoIndexPath({ owner, view: 'branches' })} aria-current={branchesView ? 'page' : undefined}>Your branches without a pull request</a>
  </nav>
</header>

<LoadingGate
  phase={index.status === 'failed' && index.failure ? 'failed' : index.status === 'ready' ? 'ready' : 'loading'}
  view={oneStep('Repositories', 'The repositories you can review', index.status === 'failed' && index.failure ? { failure: index.failure, what: 'your repositories' } : index.status === 'ready' ? 'ready' : 'loading')}
  onretry={() => void index.reload()}
>
  <div class="repo-controls">
    <input type="search" class="repo-filter" bind:value={text} placeholder="Filter by owner or name" aria-label="Filter repositories by owner or name" spellcheck="false" autocomplete="off" />
    <!-- A filter of the views, not another page: the owner chosen is the current one of its set (aria-current=true), so the views mark the one current page. -->
    <nav class="owner-chips" aria-label="Owners">
      <a class="chip" href={repoIndexPath({ view: branchesView ? 'branches' : null })} aria-current={owner ? undefined : 'true'}>Everyone</a>
      {#each index.owners as o (o.login)}
        <a class="chip" href={repoIndexPath({ owner: o.login, view: branchesView ? 'branches' : null })} aria-current={owner?.toLowerCase() === o.login.toLowerCase() ? 'true' : undefined}
          >{o.login} <em>{o.count}</em></a
        >
      {/each}
    </nav>
  </div>

  <p class="muted small pin-said" role="status">{pinSaid}</p>
  {#if index.pending.length}
    <p class="muted small repo-note" role="status">Still listing the repositories of {index.pending.join(', ')}…</p>
  {/if}
  {#if index.unlisted.length}
    <p class="repo-note repo-unlisted" role="alert">
      Couldn't list the repositories of {index.unlisted.map((u) => u.login).join(', ')}; the others are shown.
      <button class="micro" onclick={() => void index.reload()}>Try again</button>
    </p>
  {/if}
  {#if index.failed.length > 1}
    <p class="repo-note repo-unlisted">
      {index.failed.length} repositories couldn't be counted.
      <button class="micro retry-all" onclick={() => void index.retryFailed()}>Try again for all {index.failed.length}</button>
    </p>
  {/if}
  {#if branchesView}
    <p class="muted small repo-note" role="status">
      {#if checking}Looking into {checking} of the {candidates.length} repositories you pushed to recently or pinned…{:else}Among the repositories you pushed to in the last 30 days, or pinned.{/if}
    </p>
  {/if}

  {#each sections as sec (sec.id)}
    <h2 class="repo-group">{sec.title}</h2>
    <ul class="pr-list">
      {#each sec.rows as r (r.key)}
        {@render row(r, stateOf(r))}
      {/each}
    </ul>
  {:else}
    <p class="muted empty-line">
      {#if branchesView && checking}Looking for your branches…{:else if index.pending.length}{text || owner || branchesView ? 'No matches yet' : 'No repositories yet'}: still listing the repositories of {index.pending.join(', ')}…{:else if branchesView && !candidates.length}You haven't pushed to any repository here in the last 30 days, nor pinned one.{:else if branchesView}No branches of yours without a pull request in the repositories you pushed to recently.{:else if text || owner}No repository matches.{:else if index.unlisted.length}No repositories to show from the accounts that could be listed.{:else}No repositories to show: install the Changeling App where your repositories are.{/if}
    </p>
  {/each}
</LoadingGate>

<style>
  .repo-views {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }
  .repo-views a[aria-current='page'],
  .chip[aria-current='true'] {
    border-color: var(--accent);
    color: var(--accent);
  }
  .repo-controls {
    display: flex;
    flex-wrap: wrap;
    gap: 10px 16px;
    align-items: center;
    margin: 0 0 16px;
  }
  .repo-filter {
    width: 22em;
    max-width: 100%;
  }
  .owner-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }
  .chip {
    font-size: 12px;
    border: 1px solid var(--line);
    border-radius: 30px;
    padding: 3px 10px;
    color: var(--fg);
    text-decoration: none;
    white-space: nowrap;
  }
  .chip em {
    font-style: normal;
    color: var(--muted);
  }
  .repo-group {
    font-size: 13px;
    font-weight: 600;
    color: var(--muted);
    margin: 18px 0 8px;
  }
  .repo-note {
    margin: 0 0 12px;
  }
  .pin-said {
    margin: 0 0 12px;
  }
  /* Rendered while empty, so a screen reader hears what it comes to say. */
  .pin-said:empty {
    margin: 0;
  }
  .repo-unlisted {
    font-size: 13px;
    color: var(--bad);
  }
  .repo-why {
    margin: 4px 0 0;
    font-size: 12px;
    color: var(--muted);
  }
  .turn {
    color: var(--accent);
  }
  .checks.good {
    color: var(--good);
  }
  .checks.bad {
    color: var(--bad);
  }
  .checks.pending {
    color: var(--warn);
  }
  .pin {
    font-size: 13px;
  }
  /* The pin stays beside its row at phone width too (rows elsewhere stack their side column). */
  @media (max-width: 760px) {
    .repo-row {
      grid-template-columns: 18px minmax(0, 1fr) auto;
    }
  }
</style>
